import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Activity, MousePointer, Keyboard, TrendingUp, Calendar, ChevronDown, ChevronRight, Image as ImageIcon, Ticket, AlertTriangle, Copy } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { getActivitySegmentsByUidAndDateRange, type ActivitySegment } from '@/api/activitySegment';
import { getScreenshotsByAdmin } from '@/api/trackingTime';
import { createTicket } from '@/api/ticket';
import { polishContent } from '@/api/openai';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import toastify from '@/utils/toastify';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';

interface DailyActivitySummaryProps {
  uid: string;
  date: string; // YYYY-MM-DD format
}

interface ScreenshotInfo {
  url: string;
  key: string;
  timestamp: string;
  time: string;
}

const DailyActivitySummary: React.FC<DailyActivitySummaryProps> = ({ uid, date }) => {
  const [segments, setSegments] = useState<ActivitySegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedHours, setExpandedHours] = useState<Record<string, boolean>>({});
  const [screenshots, setScreenshots] = useState<ScreenshotInfo[]>([]);
  const [showGallery, setShowGallery] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketScreenshots, setTicketScreenshots] = useState<ScreenshotInfo[]>([]);
  const [selectedScreenshotKeys, setSelectedScreenshotKeys] = useState<Set<string>>(new Set());
  const [ticketSegment, setTicketSegment] = useState<ActivitySegment | null>(null);
  const [includeScreenshots, setIncludeScreenshots] = useState(true);
  const [includeTotalKeyboard, setIncludeTotalKeyboard] = useState(true);
  const [includeTotalMouse, setIncludeTotalMouse] = useState(true);
  const [includeKeySequence, setIncludeKeySequence] = useState(true);
  const [ticketReason, setTicketReason] = useState('');
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [isPolishing, setIsPolishing] = useState(false);

  const galleryItems = useMemo(
    () =>
      screenshots.map((shot) => ({
        original: shot.url,
        thumbnail: shot.url,
        originalAlt: shot.time || 'Screenshot',
        thumbnailAlt: shot.time || 'Screenshot',
      })),
    [screenshots],
  );

  const handleOpenTicketModal = (segmentScreens: ScreenshotInfo[], segment: ActivitySegment) => {
    setTicketScreenshots(segmentScreens);
    setTicketSegment(segment);
    // Initialize all screenshots as selected
    setSelectedScreenshotKeys(new Set(segmentScreens.map((s) => s.key)));
    // Initialize all options as selected by default
    setIncludeScreenshots(true);
    setIncludeTotalKeyboard(true);
    setIncludeTotalMouse(true);
    setIncludeKeySequence(true);
    setTicketReason('');
    setShowTicketModal(true);
  };

  const handleSubmitTicket = async () => {
    const selectedScreenshots = ticketScreenshots.filter((s) => selectedScreenshotKeys.has(s.key));
    const hasAnySelection =
      (includeScreenshots && selectedScreenshots.length > 0) ||
      includeTotalKeyboard ||
      includeTotalMouse ||
      includeKeySequence;
    
    if (!uid || !hasAnySelection || !ticketReason.trim()) {
      toastify.error('Please provide a reason and ensure at least one item is selected.');
      return;
    }
    try {
      setIsSubmittingTicket(true);
      await createTicket({
        uid,
        urlImages: includeScreenshots ? selectedScreenshots.map((s) => s.url) : [],
        reason: ticketReason.trim(),
        includeScreenshots: includeScreenshots && selectedScreenshots.length > 0,
        includeTotalKeyboard,
        includeTotalMouse,
        includeKeySequence,
        totalKeyboard: includeTotalKeyboard && ticketSegment ? ticketSegment.totalKeyboardCount : undefined,
        totalMouse: includeTotalMouse && ticketSegment ? ticketSegment.totalMouseCount : undefined,
        keySequence: includeKeySequence && ticketSegment ? ticketSegment.keySequence : undefined,
      });
      toastify.success('Ticket created successfully.');
      setShowTicketModal(false);
      setTicketScreenshots([]);
      setTicketSegment(null);
      setSelectedScreenshotKeys(new Set());
      setTicketReason('');
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error creating ticket from activity summary:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create ticket. Please try again.';
      toastify.error(errorMessage);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const handlePolishContent = async () => {
    if (!ticketReason.trim()) {
      toastify.error('Please enter a reason before polishing.');
      return;
    }

    setIsPolishing(true);
    try {
      const polished = await polishContent({ text: ticketReason });
      if (polished) {
        setTicketReason(polished);
        toastify.success('Content polished successfully.');
      } else {
        toastify.error('Failed to polish content. Please try again.');
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error polishing content:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to polish content. Please try again.';
      toastify.error(errorMessage);
    } finally {
      setIsPolishing(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!uid || !date) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const startDate = new Date(date);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(date);
        endDate.setHours(23, 59, 59, 999);

        const [segmentData, screenshotData] = await Promise.all([
          getActivitySegmentsByUidAndDateRange(
          uid,
          startDate.toISOString(),
          endDate.toISOString()
          ),
          getScreenshotsByAdmin({ uid, date }),
        ]);

        setSegments(segmentData);
        setScreenshots(screenshotData.screenshots || []);
      } catch (err: any) {
        console.error('Error loading daily activity segments:', err);
        setError(err?.response?.data?.message || 'Failed to load activity data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [uid, date]);

  // Deduplicate segments by _id before grouping (must be before early returns)
  const uniqueSegments = useMemo(() => {
    const seen = new Set<string>();
    return segments.filter((segment) => {
      const id = segment._id;
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }, [segments]);

  // Group segments by hour for better visualization
  const segmentsByHour = useMemo(() => {
    return uniqueSegments.reduce((acc, segment) => {
      const hour = format(new Date(segment.segmentStartTime), 'HH:00');
      if (!acc[hour]) {
        acc[hour] = [];
      }
      acc[hour].push(segment);
      return acc;
    }, {} as Record<string, ActivitySegment[]>);
  }, [uniqueSegments]);

  // Calculate overall statistics for the day
  const totalSegments = uniqueSegments.length;
  const totalActiveMinutes = uniqueSegments.reduce((sum, s) => sum + s.activeMinutes, 0);
  // Calculate actual duration of each segment instead of assuming 10 minutes
  const totalPossibleMinutes = uniqueSegments.reduce((sum, s) => {
    const start = new Date(s.segmentStartTime);
    const end = new Date(s.segmentEndTime);
    const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
    return sum + durationMinutes;
  }, 0);
  const overallActivityLevel = totalPossibleMinutes > 0 
    ? (totalActiveMinutes / totalPossibleMinutes) * 100 
    : 0;
  const totalKeyboard = uniqueSegments.reduce((sum, s) => sum + s.totalKeyboardCount, 0);
  const totalMouse = uniqueSegments.reduce((sum, s) => sum + s.totalMouseCount, 0);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily Activity Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily Activity Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (uniqueSegments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Daily Activity Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No activity data available for {format(new Date(date), 'dd/MM/yyyy')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Daily Activity Summary - {format(new Date(date), 'dd/MM/yyyy')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Overall Activity
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-4 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    overallActivityLevel >= 70
                      ? 'bg-green-500'
                      : overallActivityLevel >= 50
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${overallActivityLevel}%` }}
                />
              </div>
              <span className="text-sm font-medium">
                {totalActiveMinutes}/{totalPossibleMinutes} min
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {overallActivityLevel.toFixed(1)}% active
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Activity className="h-3 w-3" />
              Total Segments
            </p>
            <p className="text-lg font-semibold">{totalSegments}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Keyboard className="h-3 w-3" />
              Total Keyboard
            </p>
            <p className="text-lg font-semibold">{totalKeyboard.toLocaleString()}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <MousePointer className="h-3 w-3" />
              Total Mouse
            </p>
            <p className="text-lg font-semibold">{totalMouse.toLocaleString()}</p>
          </div>
        </div>

        {/* Hourly Breakdown */}
        <div>
          <h4 className="text-sm font-medium mb-3">Activity by Hour</h4>
          <ScrollArea className="h-[400px] w-full rounded-md border p-4">
            <div className="space-y-4">
              {Object.entries(segmentsByHour)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([hour, hourSegments]) => {
                  const hourActiveMinutes = hourSegments.reduce((sum, s) => sum + s.activeMinutes, 0);
                  // Calculate actual duration of each segment instead of assuming 10 minutes
                  const hourPossibleMinutes = hourSegments.reduce((sum, s) => {
                    const start = new Date(s.segmentStartTime);
                    const end = new Date(s.segmentEndTime);
                    const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
                    return sum + durationMinutes;
                  }, 0);
                  const hourActivityLevel = hourPossibleMinutes > 0 
                    ? (hourActiveMinutes / hourPossibleMinutes) * 100 
                    : 0;
                  const hourKeyboard = hourSegments.reduce((sum, s) => sum + s.totalKeyboardCount, 0);
                  const hourMouse = hourSegments.reduce((sum, s) => sum + s.totalMouseCount, 0);
                  const isExpanded = expandedHours[hour];

                  return (
                    <div key={hour} className="p-3 rounded-md border bg-slate-50 space-y-2">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium">{hour}</p>
                          <p className="text-xs text-muted-foreground">
                            {hourSegments.length} segment{hourSegments.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <Badge
                          variant={hourActivityLevel >= 70 ? 'default' : hourActivityLevel >= 50 ? 'secondary' : 'outline'}
                        >
                          {hourActiveMinutes}/{hourPossibleMinutes} min
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                hourActivityLevel >= 70
                                  ? 'bg-green-500'
                                  : hourActivityLevel >= 50
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                              style={{ width: `${hourActivityLevel}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {hourActivityLevel.toFixed(0)}%
                          </span>
                        </div>
                        <div className="flex gap-4 text-xs text-muted-foreground">
                          <span>
                            <Keyboard className="h-3 w-3 inline mr-1" />
                            {hourKeyboard}
                          </span>
                          <span>
                            <MousePointer className="h-3 w-3 inline mr-1" />
                            {hourMouse}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setExpandedHours((prev) => ({ ...prev, [hour]: !isExpanded }))
                          }
                          className="flex items-center gap-1 text-xs"
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          {isExpanded ? 'Hide segments' : 'View segments'}
                        </Button>
                      </div>
                      {isExpanded && (
                        <div className="space-y-2 border-t pt-2">
                          {hourSegments
                            .slice()
                            .sort(
                              (a, b) =>
                                new Date(a.segmentStartTime).getTime() -
                                new Date(b.segmentStartTime).getTime(),
                            )
                            .map((segment, idx) => {
                              const start = new Date(segment.segmentStartTime);
                              const end = new Date(segment.segmentEndTime);
                              const durationMinutes = Math.max(
                                1,
                                Math.round((end.getTime() - start.getTime()) / (1000 * 60)),
                              );
                              const segmentActivity =
                                durationMinutes > 0
                                  ? (segment.activeMinutes / durationMinutes) * 100
                                  : 0;

                              const segmentScreenshots = screenshots.filter((shot) => {
                                const shotTime = new Date(shot.timestamp);
                                return shotTime >= start && shotTime <= end;
                              });

                              return (
                                <div
                                  key={`${segment._id || 'segment'}-${hour}-${idx}`}
                                  className="rounded-md border bg-white p-3 shadow-sm"
                                >
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium">
                                        {format(start, 'HH:mm')} - {format(end, 'HH:mm')}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        Duration: {durationMinutes} min • Active: {segment.activeMinutes} min
                                      </p>
                                    </div>
                                    <Badge
                                      variant={
                                        segmentActivity >= 70
                                          ? 'default'
                                          : segmentActivity >= 50
                                          ? 'secondary'
                                          : 'outline'
                                      }
                                    >
                                      {segmentActivity.toFixed(0)}%
                                    </Badge>
                                  </div>
                                  <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div
                                          className={`h-full transition-all ${
                                            segmentActivity >= 70
                                              ? 'bg-green-500'
                                              : segmentActivity >= 50
                                              ? 'bg-yellow-500'
                                              : 'bg-red-500'
                                          }`}
                                          style={{ width: `${segmentActivity}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-muted-foreground">
                                        {segmentActivity.toFixed(0)}%
                                      </span>
                                    </div>
                                    <div className="flex gap-4 text-xs text-muted-foreground">
                                      <span>
                                        <Keyboard className="h-3 w-3 inline mr-1" />
                                        {segment.totalKeyboardCount}
                                      </span>
                                      <span>
                                        <MousePointer className="h-3 w-3 inline mr-1" />
                                        {segment.totalMouseCount}
                                      </span>
                                    </div>
                                    {/* Pattern Detection / Key Sequence Evidence */}
                                    {(segment.repetitivePattern || segment.keySequence) && (
                                      <div
                                        className={`mt-2 p-2 rounded-md border ${
                                          segment.repetitivePattern
                                            ? 'bg-yellow-50 border-yellow-200'
                                            : 'bg-blue-50 border-blue-200'
                                        }`}
                                      >
                                        <div className="flex items-start gap-2">
                                          {segment.repetitivePattern ? (
                                            <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                          ) : (
                                            <Keyboard className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                          )}
                                          <div className="flex-1 min-w-0">
                                            <p className={`text-xs font-medium ${
                                              segment.repetitivePattern
                                                ? 'text-yellow-800'
                                                : 'text-blue-800'
                                            }`}>
                                              {segment.repetitivePattern
                                                ? 'Suspicious Pattern Detected'
                                                : 'Key Sequence Recorded'}
                                            </p>
                                            {segment.repetitivePattern && (
                                              <p className="text-xs text-yellow-700 mt-1">
                                                Pattern: <span className="font-mono font-semibold">{segment.repetitivePattern}</span>
                                                {segment.patternRepetitions && (
                                                  <span> (repeated {segment.patternRepetitions} times)</span>
                                                )}
                                              </p>
                                            )}
                                            {segment.keySequence && (
                                              <div className="mt-2">
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                  <p className={`text-xs font-medium ${
                                                    segment.repetitivePattern
                                                      ? 'text-yellow-800'
                                                      : 'text-blue-800'
                                                  }`}>
                                                    Key Sequence Evidence:
                                                  </p>
                                                  <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-5 px-2 text-[10px]"
                                                    onClick={() => {
                                                      navigator.clipboard.writeText(segment.keySequence || '');
                                                      toastify.success('Key sequence copied to clipboard');
                                                    }}
                                                  >
                                                    <Copy className="h-3 w-3 mr-1" />
                                                    Copy
                                                  </Button>
                                                </div>
                                                <div className={`bg-white border rounded p-2 max-h-32 overflow-y-auto ${
                                                  segment.repetitivePattern
                                                    ? 'border-yellow-300'
                                                    : 'border-blue-300'
                                                }`}>
                                                  <p className="text-xs font-mono text-gray-800 break-all whitespace-pre-wrap">
                                                    {segment.keySequence}
                                                  </p>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {segmentScreenshots.length > 0 && (
                                      <div className="mt-2 space-y-2">
                                        <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                                          <ImageIcon className="h-3 w-3" />
                                          <span>
                                            Screenshots ({segmentScreenshots.length})
                                          </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {segmentScreenshots.map((shot) => {
                                            const index = screenshots.findIndex((s) => s.key === shot.key);
                                            return (
                                              <button
                                                key={shot.key}
                                                type="button"
                                                className="border rounded-md overflow-hidden w-20 h-14 hover:ring-2 hover:ring-primary focus:outline-none"
                                                onClick={() => {
                                                  if (index >= 0) {
                                                    setSelectedImageIndex(index);
                                                  }
                                                  setShowGallery(true);
                                                }}
                                              >
                                                <img
                                                  src={shot.url}
                                                  alt={shot.time || 'Screenshot'}
                                                  className="w-full h-full object-cover"
                                                />
                                              </button>
                                            );
                                          })}
                                        </div>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="h-7 px-3 text-xs"
                                          onClick={() => handleOpenTicketModal(segmentScreenshots, segment)}
                                        >
                                          <Ticket className="h-3 w-3 mr-1" />
                                          Create Ticket
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </ScrollArea>
        </div>
      </CardContent>
      {showGallery && galleryItems.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-[70] flex items-center justify-center">
          <div className="w-full max-w-4xl mx-4 relative">
            <style>
              {`
                .image-gallery-slide {
                  height: 75vh !important;
                }
                .image-gallery-slide img {
                  height: 100% !important;
                  object-fit: contain !important;
                }
                .image-gallery-thumbnails-container {
                  height: 20vh !important;
                }
                .image-gallery-thumbnail {
                  height: 100% !important;
                }
                .image-gallery-thumbnail img {
                  height: 100% !important;
                  object-fit: cover !important;
                }
              `}
            </style>
            <ImageGallery
              items={galleryItems}
              startIndex={selectedImageIndex}
              showPlayButton={false}
              showFullscreenButton={true}
              showThumbnails={true}
              showNav={true}
              showIndex={true}
            />
            <Button
              className="absolute top-4 right-4 bg-white text-black hover:bg-gray-200"
              onClick={() => setShowGallery(false)}
            >
              Close
            </Button>
          </div>
        </div>
      )}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Ticket from Activity Segment</DialogTitle>
            <DialogDescription>
              Enter the reason for this ticket. Select which items to include (screenshots, keyboard/mouse counts, key sequence).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {ticketScreenshots.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    Screenshots to attach ({selectedScreenshotKeys.size}/{ticketScreenshots.length})
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => {
                        if (selectedScreenshotKeys.size === ticketScreenshots.length) {
                          setSelectedScreenshotKeys(new Set());
                        } else {
                          setSelectedScreenshotKeys(new Set(ticketScreenshots.map((s) => s.key)));
                        }
                      }}
                    >
                      {selectedScreenshotKeys.size === ticketScreenshots.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded-md p-2">
                  {ticketScreenshots.map((shot) => {
                    const isSelected = selectedScreenshotKeys.has(shot.key);
                    return (
                      <div
                        key={shot.key}
                        className="relative cursor-pointer"
                        onClick={() => {
                          const newSelected = new Set(selectedScreenshotKeys);
                          if (isSelected) {
                            newSelected.delete(shot.key);
                          } else {
                            newSelected.add(shot.key);
                          }
                          setSelectedScreenshotKeys(newSelected);
                        }}
                      >
                        <div className={`relative border-2 rounded ${
                          isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                        }`}>
                          <img
                            src={shot.url}
                            alt={shot.time || 'Screenshot'}
                            className="w-16 h-12 object-cover rounded"
                          />
                          <div className="absolute top-1 left-1">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                const newSelected = new Set(selectedScreenshotKeys);
                                if (checked) {
                                  newSelected.add(shot.key);
                                } else {
                                  newSelected.delete(shot.key);
                                }
                                setSelectedScreenshotKeys(newSelected);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="bg-white/90"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
          {ticketSegment && (
            <div className="space-y-3 border rounded-md p-4">
              <p className="text-sm font-medium">Include in ticket:</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-screenshots-daily"
                    checked={includeScreenshots}
                    onCheckedChange={(checked) => setIncludeScreenshots(checked === true)}
                  />
                  <label htmlFor="include-screenshots-daily" className="text-sm cursor-pointer">
                    Screenshots ({selectedScreenshotKeys.size}/{ticketScreenshots.length})
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-total-keyboard-daily"
                    checked={includeTotalKeyboard}
                    onCheckedChange={(checked) => setIncludeTotalKeyboard(checked === true)}
                  />
                  <label htmlFor="include-total-keyboard-daily" className="text-sm cursor-pointer">
                    Total Keyboard ({ticketSegment.totalKeyboardCount.toLocaleString()})
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-total-mouse-daily"
                    checked={includeTotalMouse}
                    onCheckedChange={(checked) => setIncludeTotalMouse(checked === true)}
                  />
                  <label htmlFor="include-total-mouse-daily" className="text-sm cursor-pointer">
                    Total Mouse ({ticketSegment.totalMouseCount.toLocaleString()})
                  </label>
                </div>
                {ticketSegment.keySequence && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include-key-sequence-daily"
                      checked={includeKeySequence}
                      onCheckedChange={(checked) => setIncludeKeySequence(checked === true)}
                    />
                    <label htmlFor="include-key-sequence-daily" className="text-sm cursor-pointer">
                      Key Sequence Evidence
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <label htmlFor="ticket-reason" className="text-sm font-medium">
              Reason *
            </label>
              <Textarea
                id="ticket-reason"
                value={ticketReason}
                onChange={(e) => setTicketReason(e.target.value)}
                placeholder="Enter the reason for this ticket..."
                className="min-h-[120px]"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handlePolishContent}
              disabled={isPolishing || !ticketReason.trim()}
            >
              {isPolishing ? 'Polishing...' : 'Polish Content using OpenAI'}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (isSubmittingTicket) return;
                  setShowTicketModal(false);
                  setTicketScreenshots([]);
                  setTicketSegment(null);
                  setSelectedScreenshotKeys(new Set());
                  setTicketReason('');
                }}
                disabled={isSubmittingTicket}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitTicket}
                disabled={isSubmittingTicket || !ticketReason.trim()}
              >
                {isSubmittingTicket ? 'Submitting...' : 'Submit Ticket'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DailyActivitySummary;
